import { FormFieldType, TFormField } from '@models/forms';
import { engineRegExp } from '@lio/rule-engine';
import { CreatePasswordField } from '@pages/Login/SignUpView/CreatePasswordField';
import { LoginSource } from '@pages/Login/constants';
import { IndicationSummaryGetResponse } from '@lio/api-types';

export const getFormFields = ({
  loginSource,
  isEmailPrefilled = false,
}: {
  loginSource?: LoginSource;
  isEmailPrefilled?: boolean;
}): { [fieldName: string]: TFormField } => ({
  firstName: {
    gridWidth: { xs: 12 },
    name: 'firstName',
    label: 'First Name',
    fieldType: FormFieldType.TextField,
    required: true,
    helperText: '', // will suppress length helper text
    fieldConfig: {
      textField: {
        maxLength: 51, // okta max length
      },
    },
  },
  lastName: {
    gridWidth: { xs: 12 },
    name: 'lastName',
    label: 'Last Name',
    helperText: '', // will suppress length helper text
    fieldType: FormFieldType.TextField,
    required: true,
    fieldConfig: {
      textField: {
        maxLength: 51, // okta max length
      },
    },
  },
  email: {
    gridWidth: { xs: 12 },
    name: 'email',
    label: 'Email',
    fieldType: FormFieldType.TextField,
    disabled: loginSource === LoginSource.INDICATION || isEmailPrefilled,
    required: true,
    helperText:
      loginSource === LoginSource.INDICATION
        ? 'This email address is associated with the indication creator'
        : isEmailPrefilled
          ? 'This is the email address we sent your invitation to'
          : '',
    fieldConfig: {
      textField: {
        regexPattern: engineRegExp.email.regExp,
        regexMessage: 'The email address is not valid',
        autocomplete: 'new-password',
      },
    },
  },
  password: {
    gridWidth: { xs: 12 },
    name: 'password',
    label: 'Password',
    fieldType: FormFieldType.CustomField,
    required: true,
    fieldConfig: {
      customField: {
        Component: CreatePasswordField,
      },
    },
  },
});

/**
 * Partner ID accepts both a Brokerage code (`XXXX-XXXX`, letters and digits, e.g. `AB2C-D3EF`) and a
 * legacy numeric OneShield partner code. Uppercases, drops anything but letters, digits and hyphens,
 * and adds the hyphen to an 8-character code that has letters, since the API matches codes exactly.
 * All-digit input is left as typed so legacy codes keep working.
 */
export const normalizePartnerIdInput = (value: string): string => {
  const cleaned = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  if (!cleaned.includes('-') && cleaned.length === 8 && /[A-Z]/.test(cleaned)) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  }
  return cleaned;
};

export const getPartnerFields = ({
  indicationSummary,
  isPartnerIdPrefilled = false,
}: {
  indicationSummary?: IndicationSummaryGetResponse;
  isPartnerIdPrefilled?: boolean;
}): { [fieldName: string]: TFormField } => ({
  partnerId: {
    gridWidth: { xs: 12 },
    name: 'partnerId',
    label: 'Partner ID',
    fieldType: FormFieldType.TextField,
    disabled: !!indicationSummary || isPartnerIdPrefilled,
    required: true,
    helperText: indicationSummary
      ? `This is associated with ${indicationSummary.brokerage?.name}`
      : isPartnerIdPrefilled
        ? "Your brokerage's Partner ID"
        : '',
    fieldConfig: {
      textField: {
        handleOnPattern: normalizePartnerIdInput,
      },
    },
  },
});
